/**
 * Scadenze e allerte — che cosa e' scaduto, che cosa sta per scadere.
 *
 * Perche' una pagina sola
 * -----------------------
 * Erano due: lo Scadenzario guardava avanti, le Allerte indietro. La divisione
 * era di tempo, ma chi apriva l'una cercava spesso nell'altra, e lo stesso
 * obbligo compariva in entrambe con due gesti diversi. Ora in testa ci sono i
 * numeri per tipo, che sono anche i filtri, e sotto due schede:
 *
 * * **Da gestire** — cio' che e' gia' scaduto o mancante, con i gesti per
 *   rimediare: decidere se un sostituto sia dovuto, caricarlo, sostituire un
 *   file scaduto, archiviare, spiegare sul portale perche' un file non c'e'.
 * * **In arrivo** — cio' che scadra' entro l'orizzonte scelto.
 *
 * Che cosa questa pagina non pretende di sapere
 * ---------------------------------------------
 * Se un sostituto sia dovuto. La periodicita' degli obblighi non e' un dato che
 * il sistema possieda — non e' nel seed ANAC, non e' nel catalogo, e le fonti
 * ANAC danno «attualita' del dato» come criterio senza fissare termini. Ogni
 * riga dichiara percio' la provenienza della propria cadenza, e la risposta la
 * da' l'operatore: usandola, il Comune costruisce la classificazione
 * sostitutivo/accumulativo che nessun catalogo fornisce.
 *
 * Il falso allarme di massa
 * -------------------------
 * Il progetto lo ha gia' avuto una volta — «121 in scadenza» sul cruscotto di
 * un ente appena avviato — e i presidi sono elencati in `lib/allerte.ts`. Qui se
 * ne vedono due: su un ente in avviamento gli obblighi scoperti restano fuori
 * dall'elenco finche' non si preme il loro numero, e il filtro «solo da
 * decidere» e' acceso per difetto.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  archivia,
  archiviaDocumento,
  caricaDocumentoSuNodo,
  dichiaraRegola,
  elencaPubblicazioni,
  elencaValutazioni,
  ErroreApi,
  registraValutazione,
  revocaValutazione,
  sostituisciDocumento,
} from "../api/client";
import type {
  Documento,
  IdNodo,
  Pubblicazione,
  RichiestaRegola,
  RichiestaValutazione,
  TipoAllerta,
  Valutazione,
} from "../api/types";
import { useAutenticazione } from "../auth/contesto";
import { EsitoAccessibilita } from "../components/EsitoAccessibilita";
import { ModaleCaricamento } from "../components/ModaleCaricamento";
import { ModaleDurata } from "../components/ModaleDurata";
import { ModaleNotaAssenza } from "../components/ModaleNotaAssenza";
import { ModaleRegoleObbligo } from "../components/ModaleRegoleObbligo";
import { ModaleScadenza } from "../components/ModaleScadenza";
import { ModaleVerdetto } from "../components/ModaleVerdetto";
import { Avviso, Bottone, ChipNodo, Scheda, Scheletro, StatoVuoto } from "../components/primitive";
import type { Tono } from "../components/primitive";
import { TabellaAllerte } from "../components/TabellaAllerte";
import type { AzioniAllerta } from "../components/TabellaAllerte";
import { TabelleScadenze } from "../components/TabelleScadenze";
import { useAlberatura } from "../dati/contesto-alberatura";
import { IntestazionePagina } from "../layout/Guscio";
import { catenePerId, partiPercorso, SEPARATORE_PERCORSO } from "../lib/alberatura";
import {
  allerteDaGestire,
  allerteVisibili,
  calcolaAllerte,
  conteggiPerTipo,
  eEnteInAvvio,
  eInArrivo,
  ETICHETTA_TIPO_ALLERTA,
  filtroDaParametro,
  GIORNI_TOLLERANZA_SUCCESSIONE,
  naturaPertinente,
  ORDINE_TIPI_ALLERTA,
  proposteDaConfermare,
  raggruppaPerTipo,
  riepilogoArchiviazione,
} from "../lib/allerte";
import type { Allerta, EsitoArchiviazione, FiltroAllerte } from "../lib/allerte";
import { oggi } from "../lib/formats";
import {
  cadenzaConFonte,
  calcolaScadenze,
  documentiOltreLaFineCon,
  esitoDurataDichiarata,
  indicePerIdNodo,
  regolePerNodo,
} from "../lib/scadenze";
import type {
  CadenzaRisolta,
  ScadenzaAggiornamento,
  ScadenzaArchiviazione,
  ScadenzaValidita,
} from "../lib/scadenze";
import { useRegole } from "../lib/useRegole";
import { useRisorsa } from "../lib/useRisorsa";

type Orizzonte = 30 | 90 | 365;

const ORIZZONTI: { valore: Orizzonte; etichetta: string }[] = [
  { valore: 30, etichetta: "30 giorni" },
  { valore: 90, etichetta: "90 giorni" },
  { valore: 365, etichetta: "12 mesi" },
];

/** Tono di un riquadro quando non e' a zero. */
const TONO_TIPO: Record<TipoAllerta, Tono> = {
  documento_da_sostituire: "critico",
  annualita_senza_successore: "attenzione",
  aggiornamento_scaduto: "attenzione",
  quinquennio: "neutro",
  obbligo_scoperto: "attenzione",
};

/** Obbligo di cui si stanno impostando le regole. */
interface RegoleDaImpostare {
  id_nodo: IdNodo;
  denominazione: string;
  corrente: CadenzaRisolta;
}

export function ScadenzeEAllerte() {
  const {
    ente,
    nodi,
    inCaricamento: alberaturaInCaricamento,
    errore: erroreAlberatura,
    ricarica: ricaricaAlberatura,
  } = useAlberatura();
  const { eSuperAdmin, nodiAssegnati } = useAutenticazione();
  const enteId = ente?.id ?? null;

  /**
   * Gli obblighi su cui questo operatore puo' davvero decidere.
   *
   * Per il Super Admin sono tutti; per il redattore i soli assegnati. Non e'
   * una cortesia di presentazione: un'allerta su un nodo fuori perimetro e' un
   * pulsante «Decidi» che l'API rifiuterebbe con un 403, e su un ente appena
   * avviato erano 119 righe su tre di sua competenza.
   */
  const nodiAmmessi = useMemo(
    () => (eSuperAdmin ? undefined : new Set(nodiAssegnati)),
    [eSuperAdmin, nodiAssegnati],
  );

  // Il filtro sta nell'indirizzo: il cruscotto ci arriva gia' filtrato
  // (`?tipo=obbligo_scoperto`), e un filtro che si perde ricaricando la pagina
  // e' un filtro che non si puo' condividere.
  const [parametri, setParametri] = useSearchParams();
  const filtro = filtroDaParametro(parametri.get("tipo"));
  const scegliFiltro = (scelto: FiltroAllerte) => {
    setParametri(
      (correnti) => {
        const prossimi = new URLSearchParams(correnti);
        if (filtro === scelto) prossimi.delete("tipo");
        else prossimi.set("tipo", scelto);
        return prossimi;
      },
      { replace: true },
    );
  };

  const [soloDaDecidere, setSoloDaDecidere] = useState(true);
  const [orizzonte, setOrizzonte] = useState<Orizzonte>(90);
  const [daDecidere, setDaDecidere] = useState<Allerta | null>(null);
  /** Allerta su cui si sta caricando un sostituto, con il file gia' rilasciato. */
  const [daCaricare, setDaCaricare] = useState<{ allerta: Allerta; file: File | null } | null>(
    null,
  );
  const [daSostituire, setDaSostituire] = useState<{ allerta: Allerta; documento: Documento } | null>(
    null,
  );
  const [daCommentare, setDaCommentare] = useState<Allerta | null>(null);
  const [daImpostare, setDaImpostare] = useState<RegoleDaImpostare | null>(null);
  const [durataDi, setDurataDi] = useState<{ documento: Documento; id_nodo: IdNodo } | null>(
    null,
  );
  const [scadenzaDi, setScadenzaDi] = useState<{ documento: Documento; id_nodo: IdNodo } | null>(
    null,
  );
  const [inCorso, setInCorso] = useState(false);
  const [erroreOperazione, setErroreOperazione] = useState<string | null>(null);
  const [esito, setEsito] = useState<ReactNode>(null);

  useEffect(() => {
    document.title = "Scadenze e allerte · Amministrazione trasparente";
  }, []);

  // Le pubblicazioni dell'ente che si sta guardando. Senza `ente`, al super
  // admin l'API serve quelle di tutti gli enti, e si abbinavano ai nodi di
  // questo per `id_nodo`: un obbligo pubblicato da un altro Comune risultava
  // qui con un termine invece che scoperto.
  const caricaPubblicazioni = useCallback(
    (segnale: AbortSignal) =>
      enteId === null
        ? Promise.resolve([] as Pubblicazione[])
        : elencaPubblicazioni({ ente: enteId }, segnale),
    [enteId],
  );
  const pubblicazioni = useRisorsa<Pubblicazione[]>(caricaPubblicazioni, [enteId]);

  const caricaValutazioni = useCallback(
    (segnale: AbortSignal) =>
      enteId === null
        ? Promise.resolve([] as Valutazione[])
        : elencaValutazioni({ ente: enteId }, segnale),
    [enteId],
  );
  const valutazioni = useRisorsa<Valutazione[]>(caricaValutazioni, [enteId]);

  // Le regole che il Comune ha dichiarato: natura, frequenza e durata. Lo
  // stesso hook del cruscotto, perche' i termini siano gli stessi ovunque.
  const regoleRisorsa = useRegole(enteId);
  const regole = useMemo(() => regoleRisorsa.dato ?? [], [regoleRisorsa.dato]);
  const regolaDi = useMemo(() => regolePerNodo(regole), [regole]);
  /**
   * Vero quando le dichiarazioni in vigore sono arrivate.
   *
   * Prima di allora — o se la lettura e' fallita — «Regole dell'obbligo» non si
   * offre: la modale non saprebbe che cosa c'e' gia', lo chiederebbe da capo,
   * e la dichiarazione nuova sovrascriverebbe quella del Comune senza che
   * nessuno l'abbia vista.
   */
  const regolePronte =
    !regoleRisorsa.inCaricamento && !regoleRisorsa.errore && regoleRisorsa.dato !== null;
  const titoloNonPronte = regoleRisorsa.errore
    ? "Le regole dichiarate non sono disponibili: senza, la dichiarazione in vigore non si vede."
    : "Caricamento delle regole dichiarate…";

  const elencoPubblicazioni = useMemo(() => pubblicazioni.dato ?? [], [pubblicazioni.dato]);
  const catene = useMemo(() => catenePerId(nodi), [nodi]);
  const perIdNodo = useMemo(() => indicePerIdNodo(nodi), [nodi]);

  // ------------------------------------------------------------ allerte -- //

  const allerte = useMemo(
    () =>
      calcolaAllerte(nodi, elencoPubblicazioni, valutazioni.dato ?? [], regole, { nodiAmmessi }),
    [nodi, elencoPubblicazioni, valutazioni.dato, regole, nodiAmmessi],
  );
  const daGestire = useMemo(() => allerteDaGestire(allerte), [allerte]);
  const inAvvio = useMemo(
    () => eEnteInAvvio(nodi, elencoPubblicazioni, nodiAmmessi),
    [nodi, elencoPubblicazioni, nodiAmmessi],
  );

  /**
   * Proposte di natura in attesa di conferma, per nodo: i verdetti di un
   * redattore che portano `natura_proposta` e che nessuno ha ancora tradotto
   * in regola. Senza mostrarle, il percorso «il redattore propone, il Super
   * Admin conferma» si fermava a meta'.
   */
  const propostePerNodo = useMemo(
    () => proposteDaConfermare(valutazioni.dato ?? [], regole),
    [valutazioni.dato, regole],
  );

  /** Quante volte «non dovuto» e' gia' stato detto su un obbligo. */
  const verdettiPerNodo = useMemo(() => {
    const conta = new Map<string, number>();
    for (const voce of valutazioni.dato ?? []) {
      if (voce.esito !== "sostituto_non_dovuto") continue;
      conta.set(voce.id_nodo, (conta.get(voce.id_nodo) ?? 0) + 1);
    }
    return conta;
  }, [valutazioni.dato]);

  const visibili = useMemo(
    () =>
      allerteVisibili(daGestire, {
        soloDaDecidere,
        filtro,
        inAvvio,
        eSuperAdmin,
        propostePerNodo,
      }),
    [daGestire, soloDaDecidere, filtro, inAvvio, eSuperAdmin, propostePerNodo],
  );

  const gruppi = useMemo(() => raggruppaPerTipo(visibili), [visibili]);
  const conteggi = useMemo(
    () => conteggiPerTipo(daGestire, { eSuperAdmin, propostePerNodo }),
    [daGestire, eSuperAdmin, propostePerNodo],
  );

  // ----------------------------------------------------------- in arrivo -- //

  const scadenze = useMemo(
    () => calcolaScadenze(nodi, elencoPubblicazioni, regole),
    [nodi, elencoPubblicazioni, regole],
  );
  // Solo cio' che deve ancora scadere: il gia' scaduto sta fra le allerte, con
  // i gesti per rimediare, e mostrarlo due volte in una pagina sola faceva
  // contare due volte lo stesso ritardo.
  const aggiornamenti = scadenze.filter(
    (scadenza): scadenza is ScadenzaAggiornamento =>
      scadenza.tipo === "aggiornamento" && eInArrivo(scadenza.giorni_residui, orizzonte),
  );
  const documentiInArrivo = scadenze.filter(
    (scadenza): scadenza is ScadenzaArchiviazione | ScadenzaValidita =>
      (scadenza.tipo === "archiviazione" || scadenza.tipo === "validita") &&
      eInArrivo(scadenza.giorni_residui, orizzonte),
  );
  const etichettaOrizzonte =
    ORIZZONTI.find((voce) => voce.valore === orizzonte)?.etichetta ?? `${orizzonte} giorni`;

  /** Il documento completo, per le modali: le righe ne portano solo id e titolo. */
  const documentoPerId = useMemo(() => {
    const indice = new Map<number, Documento>();
    for (const pubblicazione of elencoPubblicazioni) {
      for (const documento of pubblicazione.documenti ?? []) indice.set(documento.id, documento);
    }
    return indice;
  }, [elencoPubblicazioni]);

  // ----------------------------------------------------------- operazioni -- //

  const ricarica = () => {
    pubblicazioni.ricarica();
    valutazioni.ricarica();
    regoleRisorsa.ricarica();
  };
  /** «Aggiorna» rilegge anche gli obblighi: e' da li' che vengono note e cadenze. */
  const aggiornaTutto = () => {
    ricaricaAlberatura();
    ricarica();
  };

  const conErrore = async (azione: () => Promise<void>, ricaduta: string) => {
    setErroreOperazione(null);
    setEsito(null);
    setInCorso(true);
    try {
      await azione();
    } catch (causa) {
      setErroreOperazione(causa instanceof ErroreApi ? causa.messaggioEsteso : ricaduta);
    } finally {
      setInCorso(false);
    }
  };

  const registra = (dati: RichiestaValutazione) =>
    void conErrore(async () => {
      await registraValutazione(dati, enteId);
      setDaDecidere(null);
      setEsito("Verdetto registrato. Si rilegge con «Anche le decise».");
      ricarica();
    }, "Non e' stato possibile registrare il verdetto.");

  const dichiara = (dati: RichiestaRegola) =>
    void conErrore(async () => {
      if (!daDecidere) return;
      await dichiaraRegola(daDecidere.id_nodo, dati, enteId);
      setDaDecidere(null);
      setEsito("Natura dell'obbligo registrata: questa allerta non si riaprira' su questo nodo.");
      ricarica();
    }, "Non e' stato possibile dichiarare la natura dell'obbligo.");

  const revoca = (valutazione: Valutazione) =>
    void conErrore(async () => {
      await revocaValutazione(
        valutazione.id,
        "Revocato dalla pagina delle allerte: la decisione non riflette piu' la situazione.",
      );
      setEsito("Verdetto revocato: l'allerta e' tornata fra quelle da decidere.");
      ricarica();
    }, "Non e' stato possibile revocare il verdetto.");

  const archiviaOra = (allerta: Allerta) =>
    void conErrore(async () => {
      // Senza documenti esce la pubblicazione intera, in un colpo solo.
      if (!allerta.documenti?.length) {
        if (!allerta.pubblicazione_id) return;
        await archivia(allerta.pubblicazione_id);
        setEsito("Archiviato: esce dalla navigazione corrente e resta accessibile ai suoi indirizzi.");
        ricarica();
        return;
      }
      // Con i documenti si archiviano quelli della riga, uno per uno: gli altri
      // della stessa pubblicazione hanno una durata loro. Un rifiuto non ferma
      // gli altri, e la ricarica avviene comunque: quelli gia' archiviati lo
      // sono davvero, e la pagina non deve mostrarli ancora da archiviare.
      const esiti: EsitoArchiviazione[] = [];
      try {
        for (const documento of allerta.documenti) {
          if (documento.in_correzione) {
            esiti.push({ titolo: documento.titolo, stato: "rinviato" });
            continue;
          }
          try {
            await archiviaDocumento(documento.id);
            esiti.push({ titolo: documento.titolo, stato: "archiviato" });
          } catch (causa) {
            esiti.push({
              titolo: documento.titolo,
              stato: "non_riuscito",
              messaggio: causa instanceof ErroreApi ? causa.messaggioEsteso : "non riuscita",
            });
          }
        }
      } finally {
        ricarica();
      }
      const riepilogo = riepilogoArchiviazione(esiti);
      setEsito(riepilogo.esito);
      setErroreOperazione(riepilogo.errore);
    }, "Non e' stato possibile archiviare il contenuto.");

  const caricato = (allerta: Allerta, documento: Documento) => {
    setDaCaricare(null);
    setErroreOperazione(null);
    setEsito(
      <>
        «{documento.titolo}» caricato in bozza su «{allerta.denominazione_nodo}». Diventa pubblico
        con «Pubblica», in{" "}
        <Link to={`/documenti?nodo=${encodeURIComponent(allerta.id_nodo)}`}>Documenti e cartelle</Link>
        .
        <EsitoAccessibilita key={documento.id} documento={documento} />
      </>,
    );
    ricarica();
  };

  const sostituito = (allerta: Allerta, vecchio: Documento, nuovo: Documento) => {
    setDaSostituire(null);
    setErroreOperazione(null);
    setEsito(
      <>
        Sostituto di «{vecchio.titolo}» caricato in bozza. Diventa pubblico con «Pubblica», in{" "}
        <Link to={`/documenti?nodo=${encodeURIComponent(allerta.id_nodo)}`}>Documenti e cartelle</Link>
        ; fino ad allora online resta il file scaduto.
        <EsitoAccessibilita key={nuovo.id} documento={nuovo} />
      </>,
    );
    ricarica();
  };

  const apriRegole = (idNodo: IdNodo, denominazione: string, corrente: CadenzaRisolta) => {
    setEsito(null);
    setDaImpostare({ id_nodo: idNodo, denominazione, corrente });
  };

  /** Le regole di un obbligo da una riga che la cadenza risolta non la porta. */
  const apriRegoleDelNodo = (idNodo: IdNodo, denominazione: string) => {
    const nodo = perIdNodo.get(idNodo);
    apriRegole(
      idNodo,
      denominazione,
      cadenzaConFonte(
        nodo ?? { id_nodo: idNodo, id_padre: null },
        perIdNodo,
        regolaDi.get(idNodo)?.cadenza_dichiarata ?? undefined,
      ),
    );
  };

  const azioni: AzioniAllerta = {
    onDecidi: setDaDecidere,
    onRevoca: revoca,
    onArchivia: archiviaOra,
    onCarica: (allerta, file) => setDaCaricare({ allerta, file }),
    onSostituisci: (allerta, idDocumento) => {
      const documento = documentoPerId.get(idDocumento);
      if (documento) setDaSostituire({ allerta, documento });
    },
    onCommenta: setDaCommentare,
    onRegole: eSuperAdmin
      ? (allerta) => apriRegoleDelNodo(allerta.id_nodo, allerta.denominazione_nodo)
      : undefined,
  };

  if (alberaturaInCaricamento) {
    return (
      <div className="scheda" style={{ padding: "1.25rem" }}>
        <Scheletro righe={6} />
      </div>
    );
  }

  const inAttesa = pubblicazioni.inCaricamento || valutazioni.inCaricamento;
  const mostraDaGestire = filtro !== "in_arrivo";
  const mostraInArrivo = filtro === null || filtro === "in_arrivo";
  const quantiInArrivo = aggiornamenti.length + documentiInArrivo.length;

  return (
    <div className="pila">
      <IntestazionePagina
        sopratitolo="Redazione"
        titolo="Scadenze e allerte"
        sottotitolo="Che cosa e' scaduto o manca, e che cosa sta per scadere."
        azioni={
          <Bottone variante="contorno" onClick={aggiornaTutto}>
            Aggiorna
          </Bottone>
        }
      />

      {erroreAlberatura ? (
        <Avviso tono="critico" titolo="Alberatura non disponibile">
          {erroreAlberatura.messaggioEsteso} Senza gli obblighi scadenze e allerte non si possono
          calcolare: i numeri qui sotto non sono affidabili.
        </Avviso>
      ) : null}
      {pubblicazioni.errore ? (
        <Avviso tono="critico" titolo="Contenuti non disponibili">
          {pubblicazioni.errore.messaggioEsteso}
        </Avviso>
      ) : null}
      {valutazioni.errore ? (
        <Avviso tono="critico" titolo="Giornale delle decisioni non disponibile">
          {valutazioni.errore.messaggioEsteso} Non si sa quali allerte siano gia' state decise:
          meglio non decidere due volte la stessa cosa.
        </Avviso>
      ) : null}
      {regoleRisorsa.errore ? (
        <Avviso tono="critico" titolo="Regole dichiarate non disponibili">
          {regoleRisorsa.errore.messaggioEsteso} I termini sono calcolati sulle sole ipotesi di
          cadenza.
        </Avviso>
      ) : null}
      {erroreOperazione ? (
        <Avviso tono="critico" titolo="Operazione non riuscita">
          {erroreOperazione}
        </Avviso>
      ) : null}
      {esito ? <Avviso tono="positivo">{esito}</Avviso> : null}

      {/* I numeri per tipo sono anche i filtri: premuto, un riquadro lascia in
          tabella le sole righe che conta; premuto di nuovo, le rimette tutte. */}
      <div className="griglia-kpi" role="group" aria-label="Filtra per tipo">
        {ORDINE_TIPI_ALLERTA.map((tipo) => {
          const valore = conteggi[tipo];
          return (
            <button
              key={tipo}
              type="button"
              className={`kpi kpi--${valore > 0 ? TONO_TIPO[tipo] : "neutro"}`}
              aria-pressed={filtro === tipo}
              onClick={() => scegliFiltro(tipo)}
            >
              <span className="kpi__etichetta">{ETICHETTA_TIPO_ALLERTA[tipo]}</span>
              <span className="kpi__valore">{valore}</span>
            </button>
          );
        })}
        <button
          type="button"
          className="kpi kpi--neutro"
          aria-pressed={filtro === "in_arrivo"}
          onClick={() => scegliFiltro("in_arrivo")}
        >
          <span className="kpi__etichetta">In arrivo entro {etichettaOrizzonte}</span>
          <span className="kpi__valore">{quantiInArrivo}</span>
        </button>
      </div>

      {inAvvio && filtro !== "obbligo_scoperto" && conteggi.obbligo_scoperto > 0 ? (
        <Avviso tono="primario" titolo="Sezione in avviamento">
          Gli obblighi ancora senza contenuti ({conteggi.obbligo_scoperto}) sono un piano di
          lavoro, non inadempienze: si vedono premendo «{ETICHETTA_TIPO_ALLERTA.obbligo_scoperto}».
        </Avviso>
      ) : null}
      {eSuperAdmin && propostePerNodo.size > 0 ? (
        <Avviso tono="attenzione">
          {propostePerNodo.size === 1
            ? "Una natura proposta da un redattore attende"
            : `${propostePerNodo.size} nature proposte da un redattore attendono`}{" "}
          la tua conferma: sono le righe con «Conferma la natura».
        </Avviso>
      ) : null}

      <details className="legenda-compatta">
        <summary>Come si calcola</summary>
        <ul className="pila-stretta campo__aiuto">
          <li>
            · La <strong>cadenza</strong> non e' nel catalogo ANAC: se il Comune non l'ha
            dichiarata si eredita dalla sezione, e la riga lo dice.
          </li>
          <li>
            · Un'annualita' e' <strong>senza successore</strong> dopo {GIORNI_TOLLERANZA_SUCCESSIONE}{" "}
            giorni dall'anno nuovo: e' una scelta prudenziale, la norma chiede solo l'attualita'
            del dato (art. 6 d.lgs. 33/2013).
          </li>
          <li>
            · La <strong>fine della pubblicazione</strong> e' per documento: cinque anni dal 1º
            gennaio successivo (art. 8 c. 3), salvo una durata diversa. Poi il documento passa
            nell'archivio e resta accessibile.
          </li>
          <li>
            · La <strong>scadenza del file</strong> la sceglie chi carica: passata, il file resta
            pubblicato ma va sostituito.
          </li>
          <li>· Non sostituisce la valutazione del responsabile della trasparenza.</li>
        </ul>
      </details>

      {mostraDaGestire ? (
        <Scheda
          titolo={`Da gestire (${visibili.length})`}
          azioni={
            <div className="riga">
              <Bottone
                variante={soloDaDecidere ? "primario" : "contorno"}
                dimensione="piccolo"
                aria-pressed={soloDaDecidere}
                onClick={() => setSoloDaDecidere(true)}
              >
                Solo da decidere
              </Bottone>
              <Bottone
                variante={soloDaDecidere ? "contorno" : "primario"}
                dimensione="piccolo"
                aria-pressed={!soloDaDecidere}
                onClick={() => setSoloDaDecidere(false)}
              >
                Anche le decise
              </Bottone>
            </div>
          }
          fitto
        >
          {inAttesa ? (
            <div style={{ padding: "1.25rem" }}>
              <Scheletro righe={5} />
            </div>
          ) : erroreAlberatura && nodi.length === 0 ? (
            // Senza alberatura «Nulla da decidere» sarebbe un falso via libera:
            // l'avviso in testa dice gia' che cosa manca.
            <div style={{ padding: "1.25rem" }}>
              <Scheletro righe={3} />
            </div>
          ) : visibili.length === 0 ? (
            <StatoVuoto
              titolo={soloDaDecidere ? "Nulla da decidere" : "Nessuna allerta"}
              testo={
                soloDaDecidere
                  ? "Ogni contenuto scaduto ha un verdetto in vigore: «Anche le decise» li mostra."
                  : "Nessun obbligo risulta scaduto o senza un contenuto piu' recente."
              }
            />
          ) : (
            <TabellaAllerte
              gruppi={gruppi}
              catene={catene}
              propostePerNodo={propostePerNodo}
              eSuperAdmin={eSuperAdmin}
              inCorso={inCorso}
              regolePronte={regolePronte}
              azioni={azioni}
            />
          )}
        </Scheda>
      ) : null}

      {mostraInArrivo ? (
        <>
          <div className="riga" role="group" aria-label="Orizzonte delle scadenze in arrivo">
            <span className="campo__etichetta">In arrivo entro</span>
            {ORIZZONTI.map((voce) => (
              <Bottone
                key={voce.valore}
                variante={orizzonte === voce.valore ? "primario" : "contorno"}
                dimensione="piccolo"
                aria-pressed={orizzonte === voce.valore}
                onClick={() => setOrizzonte(voce.valore)}
              >
                {voce.etichetta}
              </Bottone>
            ))}
          </div>
          <TabelleScadenze
            aggiornamenti={aggiornamenti}
            documenti={documentiInArrivo}
            etichettaOrizzonte={etichettaOrizzonte}
            inAttesa={pubblicazioni.inCaricamento}
            catene={catene}
            documentoPerId={documentoPerId}
            eSuperAdmin={eSuperAdmin}
            regolePronte={regolePronte}
            titoloNonPronte={titoloNonPronte}
            onRegole={apriRegole}
            onRegoleDaDocumento={(scadenza) =>
              apriRegoleDelNodo(scadenza.id_nodo, scadenza.denominazione_nodo)
            }
            onDurata={(documento, idNodo) => {
              setEsito(null);
              setDurataDi({ documento, id_nodo: idNodo });
            }}
            onScadenza={(documento, idNodo) => {
              setEsito(null);
              setScadenzaDi({ documento, id_nodo: idNodo });
            }}
          />
        </>
      ) : null}

      {daDecidere ? (
        <ModaleVerdetto
          key={daDecidere.chiave}
          allerta={daDecidere}
          eSuperAdmin={eSuperAdmin}
          verdettiPrecedenti={verdettiPerNodo.get(daDecidere.id_nodo) ?? 0}
          proposta={
            naturaPertinente(daDecidere.tipo) ? propostePerNodo.get(daDecidere.id_nodo) : undefined
          }
          inCorso={inCorso}
          errore={erroreOperazione}
          onChiudi={() => setDaDecidere(null)}
          onRegistra={registra}
          onDichiara={dichiara}
        />
      ) : null}

      {daCaricare ? (
        <ModaleCaricamento
          key={daCaricare.allerta.chiave}
          titolo={`Carica su «${daCaricare.allerta.denominazione_nodo}»`}
          contesto={
            <ChipNodo
              {...partiPercorso(catene, daCaricare.allerta.id_nodo)}
              separatore={SEPARATORE_PERCORSO}
            />
          }
          fileIniziale={daCaricare.file}
          conScadenza
          onInvia={(file, titolo, { validoFinoAl }) =>
            caricaDocumentoSuNodo(daCaricare.allerta.id_nodo, file, titolo, { enteId, validoFinoAl })
          }
          onChiudi={() => setDaCaricare(null)}
          onCompletato={(documento) => caricato(daCaricare.allerta, documento)}
        />
      ) : null}

      {daSostituire ? (
        <ModaleCaricamento
          key={daSostituire.documento.id}
          titolo={`Sostituisci «${daSostituire.documento.titolo}»`}
          contesto={
            <ChipNodo
              {...partiPercorso(catene, daSostituire.allerta.id_nodo)}
              separatore={SEPARATORE_PERCORSO}
            />
          }
          titoloFisso={daSostituire.documento.titolo}
          formatoAttuale={daSostituire.documento.formato}
          copie={daSostituire.documento.copie_collegate ?? []}
          conScadenza
          onInvia={(file, _titolo, { ancheCopie, validoFinoAl }) =>
            sostituisciDocumento(daSostituire.documento.id, file, { ancheCopie, validoFinoAl })
          }
          onChiudi={() => setDaSostituire(null)}
          onCompletato={(nuovo) => sostituito(daSostituire.allerta, daSostituire.documento, nuovo)}
        />
      ) : null}

      {daCommentare ? (
        <ModaleNotaAssenza
          key={daCommentare.id_nodo}
          idNodo={daCommentare.id_nodo}
          denominazione={daCommentare.denominazione_nodo}
          notaAttuale={daCommentare.nota_assenza?.testo}
          enteId={enteId}
          contesto={
            <ChipNodo
              {...partiPercorso(catene, daCommentare.id_nodo)}
              separatore={SEPARATORE_PERCORSO}
            />
          }
          onChiudi={() => setDaCommentare(null)}
          onCompletato={(nodo) => {
            setDaCommentare(null);
            setEsito(
              nodo.nota_assenza
                ? `Commento su «${nodo.denominazione}» pubblicato sul portale: resta finche' l'obbligo non avra' un contenuto.`
                : `Commento su «${nodo.denominazione}» tolto dal portale.`,
            );
            // La nota sta sul nodo, e i nodi vengono dall'alberatura.
            ricaricaAlberatura();
          }}
        />
      ) : null}

      {daImpostare ? (
        <ModaleRegoleObbligo
          key={daImpostare.id_nodo}
          denominazione={daImpostare.denominazione}
          contesto={
            <ChipNodo
              {...partiPercorso(catene, daImpostare.id_nodo)}
              separatore={SEPARATORE_PERCORSO}
            />
          }
          corrente={daImpostare.corrente}
          regola={regolaDi.get(daImpostare.id_nodo)}
          onInvia={(dati) => dichiaraRegola(daImpostare.id_nodo, dati, enteId)}
          onChiudi={() => setDaImpostare(null)}
          oltreLaFineCon={(durataAnni) =>
            documentiOltreLaFineCon(elencoPubblicazioni, daImpostare.id_nodo, durataAnni, oggi())
          }
          onCompletato={(registrata) => {
            const oltre = esitoDurataDichiarata(registrata);
            setEsito(
              `Regole di «${daImpostare.denominazione}» dichiarate: i termini si ricalcolano qui e nel cruscotto.` +
                (oltre ? ` ${oltre}` : ""),
            );
            setDaImpostare(null);
            ricarica();
          }}
        />
      ) : null}

      {durataDi ? (
        <ModaleDurata
          key={durataDi.documento.id}
          documento={durataDi.documento}
          durataObbligo={regolaDi.get(durataDi.id_nodo)?.durata_anni}
          regoleNote={regolePronte}
          contesto={
            <ChipNodo
              {...partiPercorso(catene, durataDi.id_nodo)}
              separatore={SEPARATORE_PERCORSO}
            />
          }
          onChiudi={() => setDurataDi(null)}
          onCompletato={(aggiornato) => {
            setEsito(
              aggiornato.fine_pubblicazione
                ? `«${aggiornato.titolo}» resta in pubblicazione fino al ${aggiornato.fine_pubblicazione}.`
                : `Durata di «${aggiornato.titolo}» aggiornata.`,
            );
            setDurataDi(null);
            pubblicazioni.ricarica();
          }}
        />
      ) : null}

      {scadenzaDi ? (
        <ModaleScadenza
          key={scadenzaDi.documento.id}
          documento={scadenzaDi.documento}
          contesto={
            <ChipNodo
              {...partiPercorso(catene, scadenzaDi.id_nodo)}
              separatore={SEPARATORE_PERCORSO}
            />
          }
          onChiudi={() => setScadenzaDi(null)}
          onCompletato={(aggiornato) => {
            setEsito(
              aggiornato.valido_fino_al
                ? `«${aggiornato.titolo}» va sostituito entro il ${aggiornato.valido_fino_al}.`
                : `Scadenza di «${aggiornato.titolo}» tolta.`,
            );
            setScadenzaDi(null);
            pubblicazioni.ricarica();
          }}
        />
      ) : null}
    </div>
  );
}
